import React, { useCallback, useContext, useMemo } from 'react';
import { AppContext, IAppContext } from 'appState/appContext';
import { IItemForGetShortInfo, UserRoles, ITotalPrice } from 'types';
import { useRoleAccess } from 'hooks/useRoleAccess';
import MultipleSelect from './components/MultipleSelect';
import SelectedItem from 'components/SelectedItem/SelectedItem';
import style from './IsRestrictedField.module.scss';

interface IIsRestrictedField {
	setRestrictedInRestaurants: (value: string[]) => void;
	isRestrictedInRestaurants: string[];
	totalPrices: ITotalPrice[];
	fetchedRestaurants: IItemForGetShortInfo[];
}

const IsRestrictedField: React.FC<IIsRestrictedField> = ({
	isRestrictedInRestaurants,
	totalPrices,
	fetchedRestaurants,
	setRestrictedInRestaurants,
}) => {
	const { loggedUser } = useContext<IAppContext>(AppContext);
	const hasAccess = useRoleAccess([
		UserRoles.SUPER_USER,
		UserRoles.CLIENT_MANAGER,
		UserRoles.GROUP_MANAGER,
		UserRoles.TECHNICAL,
	]);

	const handleRemove = useCallback(
		(id: string) => {
			const updatedSelectedItems = isRestrictedInRestaurants.filter((el) => el !== id);
			setRestrictedInRestaurants(updatedSelectedItems);
		},
		[isRestrictedInRestaurants, setRestrictedInRestaurants],
	);

	const restaurantsList = useMemo(() => {
		return isRestrictedInRestaurants.map((el) => {
			const accessibleRestaurantIds = loggedUser.restaurants?.map((el) => el.id);
			const label = fetchedRestaurants.find((rest) => rest.id === el)?.name || '';
			return (
				<SelectedItem
					key={el}
					label={label}
					onDelete={!hasAccess && !accessibleRestaurantIds?.includes(el) ? undefined : () => handleRemove(el)}
				/>
			);
		});
	}, [isRestrictedInRestaurants, loggedUser, fetchedRestaurants, hasAccess, handleRemove]);

	return (
		<div className={style.container} data-test="IsRestrictedField">
			<div className={style.field}>
				<MultipleSelect
					isRestrictedInRestaurants={isRestrictedInRestaurants}
					totalPrices={totalPrices}
					setRestrictedInRestaurants={setRestrictedInRestaurants}
				/>
			</div>
			<div className={style.restaurantsList}>{restaurantsList}</div>
		</div>
	);
};

export default React.memo(IsRestrictedField);
